import classNames from "classnames";
import React, { FormEvent, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import {
  usePortalPasswordSignInMutation,
  usePortalSendCodeMutation,
} from "../../../slices/PortalSlice";
import {
  PortalAlert,
  PortalAuthFrame,
  portalInput,
  portalLabel,
  portalLink,
  portalPrimary,
} from "../components/PortalUi";
import { errorMessage, isEmail } from "../portal.utils";
import {
  getPortalSession,
  getRememberedEmail,
  getRememberedFirm,
  rememberEmail,
  setPortalSession,
} from "../portalSession";

type Mode = "password" | "code";

const tab = (on: boolean) =>
  classNames(
    "h-[38px] rounded-lg text-[13.5px]",
    on
      ? "bg-white font-extrabold text-[#1F2433] shadow-[0_1px_3px_rgba(31,36,51,0.12)]"
      : "bg-transparent font-bold text-[#6B7185]",
  );

/** Client portal sign-in: a password, or a 6-digit code sent by email. */
export const PortalSignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState(getRememberedEmail());
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [forgot, setForgot] = useState(false);
  const [signIn, signInState] = usePortalPasswordSignInMutation();
  const [sendCode, sendCodeState] = usePortalSendCodeMutation();
  const firmName = getRememberedFirm();

  useEffect(() => setError(null), [mode]);

  if (getPortalSession())
    return <Navigate to={"/portal/home"} replace={true} />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const cleanEmail = email.trim();
    if (!isEmail(cleanEmail))
      return setError("Enter the email address your invitation was sent to.");
    rememberEmail(cleanEmail);
    if (mode === "password") {
      if (!password) return setError("Enter your password.");
      const result = await signIn({ email: cleanEmail, password });
      if ("error" in result) return setError(errorMessage(result.error));
      setPortalSession(result.data);
      navigate("/portal/home", { replace: true });
    } else {
      const result = await sendCode({ email: cleanEmail });
      if ("error" in result) return setError(errorMessage(result.error));
      navigate(`/portal/code?email=${encodeURIComponent(cleanEmail)}`);
    }
  };

  return (
    <PortalAuthFrame firmName={firmName}>
      <div className={"flex flex-col gap-1.5"}>
        <h1 className={"m-0 text-2xl font-extrabold tracking-[-0.4px]"}>
          Sign in
        </h1>
        <span className={"text-sm font-medium text-[#6B7185]"}>
          Upload documents and see what your accountant shared with you.
        </span>
      </div>

      {notice && <PortalAlert tone={"info"}>{notice}</PortalAlert>}

      <div
        className={"grid grid-cols-2 gap-1 rounded-[10px] bg-[#F1F2F5] p-1"}
        role={"tablist"}>
        <button
          type={"button"}
          role={"tab"}
          aria-selected={mode === "password"}
          className={tab(mode === "password")}
          onClick={() => setMode("password")}>
          Password
        </button>
        <button
          type={"button"}
          role={"tab"}
          aria-selected={mode === "code"}
          className={tab(mode === "code")}
          onClick={() => setMode("code")}>
          Email me a code
        </button>
      </div>

      <form
        className={"flex flex-col gap-[22px]"}
        onSubmit={submit}
        noValidate={true}>
        <div className={"flex flex-col gap-1.5"}>
          <label htmlFor={"portal-email"} className={portalLabel}>
            Email
          </label>
          <input
            id={"portal-email"}
            type={"email"}
            autoComplete={"email"}
            className={portalInput}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {mode === "password" ? (
          <>
            <div className={"flex flex-col gap-1.5"}>
              <div className={"flex items-baseline justify-between"}>
                <label htmlFor={"portal-password"} className={portalLabel}>
                  Password
                </label>
                <button
                  type={"button"}
                  className={classNames("text-[13px]", portalLink)}
                  onClick={() => {
                    setForgot(true);
                    setMode("code");
                  }}>
                  Forgot password?
                </button>
              </div>
              <input
                id={"portal-password"}
                type={"password"}
                autoComplete={"current-password"}
                className={classNames(portalInput, "font-medium")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && <PortalAlert>{error}</PortalAlert>}
            <button
              type={"submit"}
              className={portalPrimary}
              disabled={signInState.isLoading}>
              {signInState.isLoading ? "Signing in…" : "Sign in"}
            </button>
          </>
        ) : (
          <>
            <span
              className={
                "text-[13.5px] font-medium leading-relaxed text-[#6B7185]"
              }>
              {forgot
                ? "We'll email you a 6-digit code to sign in. Once you're in, you can set a new password from your name at the top."
                : "We'll email you a 6-digit code. No password needed. The code works for 10 minutes."}
            </span>
            {error && <PortalAlert>{error}</PortalAlert>}
            <button
              type={"submit"}
              className={portalPrimary}
              disabled={sendCodeState.isLoading}>
              {sendCodeState.isLoading ? "Sending…" : "Send me a code"}
            </button>
          </>
        )}
      </form>

      <div
        className={
          "border-t border-[#F0F1F4] pt-[18px] text-center text-[13px] font-medium text-[#6B7185]"
        }>
        First time here? Use the link in your invitation email to set up access.
      </div>
    </PortalAuthFrame>
  );
};
